import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CONTENT_GROUP_DUPLICATE, userConstants } from '../../constants';
import { card_duplicateActions } from '../../actions/card_duplicate.actions';
import { alertActions, content_groupActions } from '../../actions';
import { stageConstants } from '../../constants/stage.constants';

const useCardDuplicateHook = ({
    isModalOpen,
    titleRef,
    stageRef
}) => {
    const dispatch = useDispatch();
    const { workspace_id, content_group_id, stage_id: default_stage_id, loading, escapeable } = useSelector(state => state.card_duplicate);
    const [name, setName] = useState('Untitled');
    const [participants, setParticipants] = useState(true);
    const [labels, setLabels] = useState(true);
    const [start_date, setStartDate] = useState(true);
    const [due_date, setDueDate] = useState(true);
    const [images, setImages] = useState(true);
    const [description, setDescription] = useState(true);
    const [reminder, setReminder] = useState(true);
    const [lists, setLists] = useState(true);

    const [stage_id, setStageId] = useState(default_stage_id); // Assuming stage_id is needed for some functionality
    const [copies, setCopies] = useState(1); // Assuming copies is needed for duplication functionality

    const [dropdownMaxHeight, setDropdownMaxHeight] = useState(300);

    const card_settings_value = {
        name,
        participants,
        labels,
        start_date,
        due_date,
        images,
        description,
        lists,
        stage_id,
        num_copies: copies,
        reminder
    };


    const updateStates = (card_duplicate) => {
        if (card_duplicate?.labels !== undefined) {
            setLabels(card_duplicate.labels);
        }
        if (card_duplicate?.participants !== undefined) {
            setParticipants(card_duplicate.participants);
        }
        if (card_duplicate?.start_date !== undefined) {
            setStartDate(card_duplicate.start_date);
        }
        if (card_duplicate?.due_date !== undefined) {
            setDueDate(card_duplicate.due_date);
        }
        if (card_duplicate?.images !== undefined) {
            setImages(card_duplicate.images);
        }
        if (card_duplicate?.description !== undefined) {
            setDescription(card_duplicate.description);
        }
        if (card_duplicate?.lists !== undefined) {
            setLists(card_duplicate.lists);
        }
        if (card_duplicate?.copies !== undefined) {
            setCopies(card_duplicate.copies);
        }
        if (card_duplicate?.stage_id !== undefined) {
            const is_included = Array.isArray(card_duplicate.stages) && card_duplicate.stages.some(stage => stage.id === card_duplicate.stage_id);
            if (is_included) {
                setStageId(card_duplicate.stage_id);
            } else {
                setStageId(default_stage_id)
            }
        }
    };

    // TODO: Initial state based on user preferences or defaults

    const handleNameChange = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const new_name = e.target.value;

        setName(new_name);
    };

    const toggleIncludedFields = (e, field) => {
        e.preventDefault();
        e.stopPropagation();
        
        switch (field) {
            case 'participants':
                setParticipants(prev => !prev);
                break;
            case 'labels': 
                setLabels(prev => !prev);
                break;
            case 'start_date':
                setStartDate(prev => !prev);
                break;
            case 'due_date':
                setDueDate(prev => !prev);
                break;
            case 'images':
                setImages(prev => !prev);
                break;
            case 'description':
                setDescription(prev => !prev);
                break;
            case 'lists':
                setLists(prev => !prev);
                break;
            case 'reminder':
                setReminder(prev => !prev);
                break;
            default:
                break;
        }
    };

    const handleStageChange = (eventKey) => {
        const newStageId = eventKey;
        setStageId(newStageId);
    };

    const handleCopiesChange = (e, action = 'increment') => {
        e.preventDefault();
        e.stopPropagation();
        // Assuming action can be 'increment' or 'decrement'

        const max_copy = CONTENT_GROUP_DUPLICATE.MAX_COPY;

        setCopies(prevCopies => {
            if (action === 'increment' && prevCopies < max_copy) {
                return prevCopies + 1;
            } else if (action === 'decrement' && prevCopies > 1) {
                return prevCopies - 1;
            }
            return prevCopies;
        });
    };


    const handleCancel = () => {
        const onSuccess = (response) => {
            updateStates(response.data.data);

        };

        dispatch(card_duplicateActions.updateDuplicateSettings({ 
                                                                    workspace_id,
                                                                    params: card_settings_value,
                                                                    onSuccess
                                                            }));

        dispatch({ type: CONTENT_GROUP_DUPLICATE.CLOSE_DUPLICATE_MODAL })
        setName('Untitled');
        setStageId(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if(loading) return;

        const onBefore = (response) => {
          // Load 
          dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_LOADING });
        };

        const onSuccess = (response) => {
            // Disable Loading
            // Alert
            // Close Modal
            const { content_groups = [] } = response.data.data

            content_groups.forEach(element => {
                dispatch({ type: stageConstants.ADD_ITEM_TO_STAGE, payload: element })
                dispatch({ type: userConstants.CREATED_CONTENTGROUP, payload: { data: {content_group : element } } })
            });

            dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_SUCCESS });
            handleCancel();
        }

        const onError = (response) => {
            // Disable Loading
            // Alert Error

            const { message } = response.data;

            dispatch(alertActions.error(message))
            dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_FAILURE, payload: message });
        }

        const onCatch = (error) => {
            // Disable Loading
            // Alert Error
            dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_FAILURE, payload: console.log(error) });
        }
        
        const params = {
            onBefore,
            onSuccess,
            onError,
            onCatch,
            workspace_id,
            content_group_id,
            data: card_settings_value
        }

        dispatch(content_groupActions.cloneContentGroup(params))
    }

      useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleCancel();
            }
        };

        if (isModalOpen && escapeable) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isModalOpen, escapeable]);

    useEffect(() => {
        function updateHeight() {
            if (stageRef.current) {
                const rect = stageRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.top;
                setDropdownMaxHeight(`${spaceBelow - 20}px`); // leave 20px padding
            }
        }

        window.addEventListener("resize", updateHeight);
        updateHeight();

        return () => window.removeEventListener("resize", updateHeight);
    }, []);

    useEffect(() => {
        if(workspace_id && isModalOpen && titleRef.current){
            const onBefore = () => {
                dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_LOADING });
            };
            const onSuccess = (response) => {
                const card_duplicate = response.data.data;

                updateStates(card_duplicate);

                dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_SUCCESS });
                dispatch({ type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_SET_STAGES, payload: card_duplicate.stages });

                setTimeout(() => {
                        if (titleRef.current) {
                            titleRef.current.focus();
                            titleRef.current.select();
                        }
                }, 0);
            };

            const onError = (response) => {
                dispatch({
                    type: CONTENT_GROUP_DUPLICATE.CARD_DUPLICATE_FAILURE,
                    payload: console.log(response),
                });
            };

            const params = {
                onBefore,
                onSuccess,
                onError,
                workspace_id,
            }
                
            dispatch(card_duplicateActions.getDuplicateSettings(params));
        }
         
    }, [workspace_id, isModalOpen, titleRef]);

    return {
        name,
        handleNameChange,
        toggleIncludedFields,
        stage_id,
        handleStageChange,
        copies,
        handleCopiesChange,
        participants,
        labels,
        start_date,
        due_date,
        images,
        description,
        lists,
        reminder,
        handleCancel,
        handleSubmit,
        card_settings_value,
        workspace_id,
        updateStates,
        dropdownMaxHeight
    };
};

export default useCardDuplicateHook;